import { useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { MaskLines, Reveal, SectionHead } from "../components/Motion";
import { api, errorText } from "../lib/api";

const FORMATS = [
  { id: "executive", name: "Executive coaching", who: "For CEOs, founders and senior leaders",
    text: "One-to-one sessions that turn BPJ research into decisions about strategy, priorities and how you lead.",
    points: ["[Number] sessions over [duration]", "Diagnostic on how you measure performance", "Between-session reading from the BPJ archive"] },
  { id: "team", name: "Team performance programme", who: "For leadership and management teams",
    text: "Facilitated workshops that align the team on goals, metrics and the habits that close the gap between plan and results.",
    points: ["Team diagnostic and kick-off workshop", "[Number] follow-up sessions", "Progress review against agreed indicators"] },
  { id: "author", name: "Author & research mentoring", who: "For academics and practitioner authors",
    text: "Guidance from experienced editors on framing research for impact and preparing manuscripts for peer review.",
    points: ["Manuscript and argument review", "Writing for practitioners and executives", "Submission and revision strategy"] },
];

const STEPS = [
  ["01", "Discovery call", "A short conversation about your goals and context."],
  ["02", "Diagnostic", "We look at how performance is measured and where it stalls."],
  ["03", "Coaching sessions", "Focused sessions grounded in evidence, not opinion."],
  ["04", "Measure results", "We review progress against the indicators you chose."],
];

function EnquiryForm({ initial }) {
  const [f, setF] = useState({ name: "", email: "", organisation: "", format: initial || FORMATS[0].name, goals: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", { name: f.name, email: f.email, subject: `Coaching enquiry — ${f.format}`,
        message: `Organisation: ${f.organisation || "—"}\n\nGoals:\n${f.goals}` });
      setSent(true);
      toast.success("Thank you — we'll be in touch to arrange a discovery call.");
    } catch (err) { toast.error(errorText(err)); }
    setBusy(false);
  };
  if (sent) return <div className="bg-[var(--ink)] text-white p-10" data-testid="coaching-enquiry-success"><p className="f-serif text-4xl">Enquiry received.</p><p className="text-[#D5DAE4] mt-3">We'll reply by email to arrange a discovery call.</p></div>;
  return (
    <form onSubmit={submit} className="space-y-7" data-testid="coaching-enquiry-form">
      <div className="grid sm:grid-cols-2 gap-8">
        <label><span className="eyebrow">Name *</span><input required value={f.name} onChange={set("name")} className="field" data-testid="coaching-name-input" /></label>
        <label><span className="eyebrow">Email *</span><input required type="email" value={f.email} onChange={set("email")} className="field" data-testid="coaching-email-input" /></label>
        <label><span className="eyebrow">Organisation</span><input value={f.organisation} onChange={set("organisation")} className="field" data-testid="coaching-org-input" /></label>
        <label><span className="eyebrow">Format</span><select value={f.format} onChange={set("format")} className="field" data-testid="coaching-format-select">{FORMATS.map((x) => <option key={x.id}>{x.name}</option>)}</select></label>
      </div>
      <label className="block"><span className="eyebrow">What would you like to achieve? *</span><textarea required rows={5} value={f.goals} onChange={set("goals")} className="field resize-none" data-testid="coaching-goals-input" /></label>
      <button disabled={busy} className="btn-red h-12 px-10 font-semibold disabled:opacity-50 inline-flex items-center gap-2" data-testid="coaching-submit-btn">{busy ? "Sending…" : "Request a discovery call"} <ArrowRight size={17} /></button>
    </form>
  );
}

export default function Coaching() {
  const [pick, setPick] = useState(null);
  return (
    <div data-testid="coaching-page">
      <section className="px-5 lg:px-12 pt-14 grid lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-7">
          <p className="eyebrow mb-6">BPJ Coaching</p>
          <h1 className="f-serif text-5xl sm:text-6xl lg:text-8xl tracking-tight leading-[0.92]"><MaskLines lines={["Evidence,", <em key="c" className="font-light">applied to</em>, <span key="y" className="text-[var(--red)]">your work.</span>]} /></h1>
          <p className="text-lg text-[var(--ink-2)] mt-10 max-w-xl leading-relaxed">Coaching for leaders, teams and authors, built on the research we publish. Ideas for leaders who measure what matters — turned into practice.</p>
          <a href="#enquire" className="btn-ink h-12 px-6 inline-flex items-center gap-2 font-semibold mt-10" data-testid="coaching-hero-cta">Book a discovery call <ArrowRight size={17} /></a>
        </div>
        <Reveal className="lg:col-span-5" delay={0.3}>
          <div className="aspect-[4/5] overflow-hidden"><img src="/img/coaching-hero.jpg" alt="Coaching conversation in a bright office" className="w-full h-full object-cover grayscale-[40%]" /></div>
        </Reveal>
      </section>

      <section className="px-5 lg:px-12 pt-28">
        <SectionHead eyebrow="Formats" title="Three ways to work with us" testid="coaching-formats-head" />
        <div className="grid md:grid-cols-3 gap-px bg-[var(--line)] border rule">
          {FORMATS.map((x, i) => (
            <Reveal key={x.id} delay={i * 0.08} className="bg-[var(--paper)] p-7 sm:p-9 flex flex-col">
              <div data-testid={`coaching-format-${x.id}`} className="flex flex-col h-full">
                <p className="f-mono text-sm text-[var(--red)]">0{i + 1}</p>
                <h3 className="f-serif text-3xl mt-4 leading-tight">{x.name}</h3>
                <p className="eyebrow mt-2">{x.who}</p>
                <p className="text-[var(--ink-2)] mt-5 leading-relaxed">{x.text}</p>
                <ul className="mt-6 space-y-2.5 text-[0.95rem] flex-1">{x.points.map((p) => <li key={p} className="flex gap-2"><Check size={16} className="text-[var(--red)] mt-1 shrink-0" />{p}</li>)}</ul>
                <p className="f-serif text-xl mt-8">[Price on request]</p>
                <a href="#enquire" onClick={() => setPick(x.name)} className="btn-line h-11 mt-5 inline-flex items-center justify-center font-semibold" data-testid={`coaching-enquire-${x.id}`}>Enquire</a>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mt-28 bg-[var(--ink)] text-white relative grain overflow-hidden">
        <div className="px-5 lg:px-12 py-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4"><p className="eyebrow !text-[#C3CAD9] mb-5">How it works</p><h2 className="f-serif text-4xl sm:text-5xl leading-[1]">From diagnosis to measurable results.</h2></div>
          <ol className="lg:col-span-7 lg:col-start-6 grid sm:grid-cols-2 gap-10">
            {STEPS.map(([n, t, d], i) => (
              <Reveal key={n} as="li" delay={i * 0.08} className="border-t border-white/20 pt-5">
                <p className="f-mono text-sm text-[#9FB0D6]">{n}</p><p className="f-serif text-2xl mt-2">{t}</p><p className="text-[#D5DAE4] mt-2 leading-relaxed">{d}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section id="enquire" className="px-5 lg:px-12 pt-28 grid lg:grid-cols-12 gap-14 scroll-mt-28">
        <div className="lg:col-span-5">
          <p className="eyebrow mb-6">Enquire</p>
          <h2 className="f-serif text-4xl sm:text-5xl leading-[1] tracking-tight">Tell us what you want to improve.</h2>
          <div className="aspect-[3/2] overflow-hidden mt-10 hidden sm:block"><img src="/img/coaching-2.jpg" alt="Coaching discussion" className="w-full h-full object-cover grayscale-[40%]" loading="lazy" /></div>
        </div>
        <div className="lg:col-span-6 lg:col-start-7"><EnquiryForm key={pick || "x"} initial={pick} /></div>
      </section>
    </div>
  );
}
